import React from 'react';
import PropTypes from "prop-types";
import Fragment from "common/src/components/widget/Fragment";
import Select from "common/src/components/widget/Select";
import Input from "common/src/components/widget/Input";
import Button from "common/src/components/widget/Button";
import DateTimePicker from "common/src/components/widget/DateTimePicker";
import FloatingDateTimePicker from "common/src/components/widget/FloatingDateTimePicker";
import FloatingDateRangePicker from "common/src/components/widget/FloatingDateRangePicker";
import {useTranslation} from "react-i18next";
import {filterOperations} from "../../../model/filterOperations";

const KEYWORD_TRANSLATION_KEYS = {
    YESTERDAY: "dataset.filter.keyword.YESTERDAY",
    LAST_WEEK: "dataset.filter.keyword.LAST_WEEK",
    LAST_MONTH: "dataset.filter.keyword.LAST_MONTH",
};

// The "add a filter" form shared by StaticFilterColumnsCard and DynamicFilterColumnsCard - the
// available columns/operations differ per card (via useFilterColumnsCard's fixed mode), but the
// form itself is identical either way.
const FilterAddForm = ({state}) => {
    const {t} = useTranslation();
    const {
        tempColumn, setTempColumn,
        tempFilterOperation, setTempFilterOperation,
        tempFilterValue, setTempFilterValue,
        availableColumnOptions,
        getFilterType,
        getFilterColumnsOperations,
        onAdd,
    } = state;

    const keywordOptions = filterOperations["KEYWORD"].map(k => ({
        code: k.code,
        label: t(KEYWORD_TRANSLATION_KEYS[k.code] || k.code),
    }));

    return (
        <div className={"columns mt-3"}>
            <div className={"column is-4"}>
                <Select options={availableColumnOptions()}
                        optionsCode={"name"} optionsLabel={field => "" + field.name + " [" + field.type + "]"}
                        optionsValue={"name"}
                        selectedItem={tempColumn}
                        emptyOption={true}
                        label={t('data.column')}
                        onChange={(e) => {
                            if (!e || e === "") {
                                setTempColumn(null);
                            } else {
                                setTempColumn(e);
                            }
                            setTempFilterOperation(null);
                            setTempFilterValue(null);
                        }}/>
            </div>
            <Fragment rendered={(tempColumn !== "" && !!getFilterType() && getFilterType() !== "")}>
                <div className={"column is-2"}>
                    <Select options={getFilterColumnsOperations()} onChange={(e) => {
                        setTempFilterOperation(e);
                        setTempFilterValue(null);
                    }}
                            selectedItem={tempFilterOperation}
                            label={t('data.operation')}
                            optionsCode={"code"} optionsLabel={"code"} optionsValue={"code"}
                            error={tempFilterOperation === ""}
                    />
                </div>
            </Fragment>
            <Fragment rendered={(tempColumn !== "" && (
                getFilterType() === "NUMBER" || getFilterType() === "ARRAY_NUMBER"))}>
                <div className={"column is-4"}>
                    <Input label={t('data.value')} value={tempFilterValue} type="number" onChange={(e) => {
                        setTempFilterValue(e);
                    }}/>
                </div>
            </Fragment>
            <Fragment rendered={(tempColumn !== "" && (getFilterType() === "EPOCH_NUMBER"))}>
                <div className={"column is-4"}>
                        <DateTimePicker label={t('data.value')} type={"datetime"}
                                        useEpoch={true}
                                        onChange={(e) => {
                                            setTempFilterValue(e);
                                        }}/>
                </div>
            </Fragment>
            <Fragment rendered={(tempColumn !== "" && getFilterType() === "STRING")}>
                <div className={"column is-4"}>
                    <Input label={t('data.value')} value={tempFilterValue} type="text" onChange={(e) => {
                        setTempFilterValue(e);
                    }}/>
                </div>
            </Fragment>
            <Fragment
                rendered={(tempColumn !== "" && getFilterType() === "TIMESTAMP" && !!tempFilterOperation)}>
                <div className={"column is-4"}>
                    <Fragment rendered={tempFilterOperation === "BETWEEN"}>
                        <FloatingDateRangePicker
                            type={"datetime"}
                            startLabel={t('data.start')}
                            endLabel={t('data.end')}
                            keywordsLabel={t('dataset.filter.keywords')}
                            keywordOptions={keywordOptions}
                            value={tempFilterValue || null}
                            onChange={(value) => setTempFilterValue(value || "")}/>
                    </Fragment>

                    <Fragment rendered={tempFilterOperation !== "BETWEEN"}>
                        <FloatingDateTimePicker
                            type={"datetime"}
                            label={t('data.timestamp')}
                            keywordsLabel={t('dataset.filter.keywords')}
                            keywordOptions={keywordOptions}
                            value={tempFilterValue?.keyword
                                ? {keyword: tempFilterValue.keyword}
                                : (tempFilterValue?.timestamp ? {date: tempFilterValue.timestamp} : null)}
                            onChange={(value) => setTempFilterValue(value?.keyword
                                ? {keyword: value.keyword}
                                : {timestamp: value?.date})}/>
                    </Fragment>
                </div>
            </Fragment>
            <Fragment rendered={(tempColumn !== "" && !!getFilterType() && getFilterType() !== "")}>
                <div className={"column is-2"}>
                    <Button label={t('add')} className={"is-outlined"} onClick={onAdd}/>
                </div>
            </Fragment>
        </div>
    );
};

FilterAddForm.propTypes = {
    state: PropTypes.object.isRequired,
};

export default FilterAddForm;
